import {
  claimProtocolMetadataSchema,
  claimRequestBaseSchema,
  extensionRunIdSchema,
  extensionVersionSchema,
} from "@reactive-resume/schema";
import { createZodDto } from "nestjs-zod/dto";
import { z } from "zod";

import { jobBoardSchema } from "../../job-search.dto";

/**
 * Spec 047 — the wire contract is owned by libs/schema. The DTO narrows
 * supportedBoards/boardIdentities to the JobBoard enum while the v2 request
 * metadata (protocolVersion / extensionVersion / runId) comes from the shared
 * `claimProtocolMetadataSchema`. v2 fields stay optional WITHOUT a zod default
 * so a v1 request parses to exactly its v1 shape (contract tests pin this).
 */
export const generalClaimRequestSchema = claimRequestBaseSchema
  .extend({
    supportedBoards: z.array(jobBoardSchema).min(1),
    boardIdentities: z.record(jobBoardSchema, z.string().min(1).max(64)).optional().default({}),
  })
  .merge(claimProtocolMetadataSchema)
  // Keep malformed v2 release strings on the decision path. The server must
  // answer `blocked(update_required)` rather than turning a client-version
  // problem into a generic 400. v1 remains byte-compatible because these
  // metadata fields are optional there.
  .extend({ extensionVersion: z.string().max(32).optional() });

export const batchClaimRequestSchema = claimRequestBaseSchema
  .extend({
    supportedBoards: z.array(jobBoardSchema).min(1),
    boardIdentities: z.record(jobBoardSchema, z.string().min(1).max(64)),
    protocolVersion: z.literal(3),
    extensionVersion: extensionVersionSchema,
    runId: extensionRunIdSchema,
    batchRunId: z.string().min(1),
  })
  .strict();

/**
 * Nest DTO classes cannot extend a union type, so the HTTP boundary uses one
 * statically-shaped object and enforces the same v1/v2/v3 discrimination in a
 * refinement. The separately exported schemas remain useful to callers that
 * want a narrowed branch.
 */
export const claimRequestSchema = claimRequestBaseSchema
  .extend({
    supportedBoards: z.array(jobBoardSchema).min(1),
    boardIdentities: z.record(jobBoardSchema, z.string().min(1).max(64)).optional().default({}),
    protocolVersion: z.union([z.literal(1), z.literal(2), z.literal(3)]).optional(),
    extensionVersion: extensionVersionSchema.optional(),
    runId: extensionRunIdSchema.optional(),
    batchRunId: z.string().min(1).optional(),
  })
  .strict()
  .superRefine((request, context) => {
    if (request.protocolVersion === 3) {
      for (const field of ["extensionVersion", "runId", "batchRunId"] as const) {
        if (!request[field]) {
          context.addIssue({
            code: z.ZodIssueCode.custom,
            path: [field],
            message: `${field} is required for protocol v3`,
          });
        }
      }
      return;
    }
    if (request.batchRunId !== undefined) {
      context.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["batchRunId"],
        message: "batchRunId is valid only for protocol v3",
      });
    }
  });

export class ClaimRequestDto extends createZodDto(claimRequestSchema) {}

export { extensionInstallIdSchema } from "@reactive-resume/schema";
